import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Clock, Package, Pencil, Store, TrendingUp, Users } from "lucide-react";
import {
  getDisplayProofInsightOutlets,
  getDisplayProofInsightOutletProducts,
  getDisplayProofInsightCompetitorActivity,
  getDisplayProofInsightCompetitorIntelligence,
  getProductGroups,
} from "../../../services/api";
import { selectUser } from "../../../redux/slices/authSlice";
import { CurrencyContext } from "../../../context/CurrencyContext";
import { useNotification } from "../../../hooks/useNotification";
import ManageCompetitorsModal from "./ManageCompetitorsModal";
import OutletFilterSelect from "./OutletFilterSelect";
import OutletProductTable from "./OutletProductTable";
import CompetitorActivityPanel from "./CompetitorActivityPanel";
import CompetitorIntelligencePanel from "./CompetitorIntelligencePanel";
import ProductGroupSelect from "../../Common/ProductGroupSelect";
import "./DisplayProofInsightsView.css";

const toInputDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

// Default view is the current month so far (1st → today), so the dashboard
// lands on real data instead of an empty date-range prompt.
const buildDefaultFilters = () => {
  const today = new Date();
  return {
    startDate: toInputDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    endDate: toInputDate(today),
    outletId: "",
    productGroupId: "",
  };
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const latest = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  return new Date(a) > new Date(b) ? a : b;
};

export default function DisplayProofInsightsView() {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const user = useSelector(selectUser);
  const isSuperAdmin = user?.role === "super_admin";
  const { currency } = useContext(CurrencyContext);

  const [isManageCompetitorsOpen, setIsManageCompetitorsOpen] = useState(false);
  const [filters, setFilters] = useState(buildDefaultFilters);
  const [outletProducts, setOutletProducts] = useState([]);
  const [competitorActivity, setCompetitorActivity] = useState([]);
  const [competitorIntelligence, setCompetitorIntelligence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [productGroups, setProductGroups] = useState([]);

  const hasDateRange = Boolean(filters.startDate && filters.endDate);

  const fetchAll = useCallback(async () => {
    const filterArgs = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      outletId: filters.outletId,
      currency,
    };
    try {
      setLoading(true);
      const [ownRes, compRes] = await Promise.all([
        getDisplayProofInsightOutletProducts(filterArgs),
        getDisplayProofInsightCompetitorActivity(filterArgs),
      ]);
      setOutletProducts(ownRes?.data?.outlets || []);
      setCompetitorActivity(compRes?.data?.competitors || []);
    } catch (error) {
      setOutletProducts([]);
      setCompetitorActivity([]);
      showError(error?.response?.data?.error || t("admin.insights.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.endDate, filters.outletId, currency, showError, t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!hasDateRange) {
      setCompetitorIntelligence(null);
      return undefined;
    }
    let cancelled = false;
    setIntelligenceLoading(true);
    getDisplayProofInsightCompetitorIntelligence({
      startDate: filters.startDate,
      endDate: filters.endDate,
      outletId: filters.outletId,
      productGroupId: filters.productGroupId,
      currency,
    })
      .then((response) => {
        if (!cancelled) setCompetitorIntelligence(response?.data || null);
      })
      .catch((error) => {
        if (cancelled) return;
        setCompetitorIntelligence(null);
        showError(error?.response?.data?.error || t("admin.insights.loadFailed"));
      })
      .finally(() => {
        if (!cancelled) setIntelligenceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    hasDateRange,
    filters.startDate,
    filters.endDate,
    filters.outletId,
    filters.productGroupId,
    currency,
    showError,
    t,
  ]);

  useEffect(() => {
    let cancelled = false;
    getDisplayProofInsightOutlets()
      .then((response) => {
        if (!cancelled) setOutlets(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        if (!cancelled) setOutlets([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getProductGroups()
      .then((response) => {
        if (!cancelled) setProductGroups(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        if (!cancelled) setProductGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // KPIs — every value from real data, no fabricated price metrics.
  const kpis = useMemo(() => {
    const productIds = new Set();
    const outletIds = new Set();
    let lastActivity = null;

    outletProducts.forEach((outlet) => {
      outletIds.add(outlet.outlet_id);
      outlet.products.forEach((product) => {
        productIds.add(product.product_id);
        lastActivity = latest(lastActivity, product.last_seen);
      });
    });
    competitorActivity.forEach((competitor) => {
      competitor.outlets.forEach((outlet) => {
        outletIds.add(outlet.outlet_id);
        lastActivity = latest(lastActivity, outlet.last_seen);
      });
    });

    return [
      {
        key: "ourProducts",
        icon: <Package size={18} />,
        label: t("admin.insights.stats.ourProducts"),
        value: productIds.size.toLocaleString(),
        tone: "own",
      },
      {
        key: "competitors",
        icon: <Users size={18} />,
        label: t("admin.insights.stats.competitorsSeen"),
        value: competitorActivity.length.toLocaleString(),
        tone: "comp",
      },
      {
        key: "outlets",
        icon: <Store size={18} />,
        label: t("admin.insights.stats.outletsCovered"),
        value: outletIds.size.toLocaleString(),
      },
      {
        key: "lastActivity",
        icon: <Clock size={18} />,
        label: t("admin.insights.stats.lastActivity"),
        value: loading ? "—" : formatDate(lastActivity),
        small: true,
      },
    ];
  }, [outletProducts, competitorActivity, loading, t]);

  // "Clear" resets to the default month-to-date view, so it should only be
  // enabled when the current filters actually differ from that default.
  const hasActiveFilter = useMemo(() => {
    const defaults = buildDefaultFilters();
    return (
      filters.startDate !== defaults.startDate ||
      filters.endDate !== defaults.endDate ||
      Boolean(filters.outletId) ||
      Boolean(filters.productGroupId)
    );
  }, [filters]);

  return (
    <div className="dpi-page">
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="dpi-header">
        <div className="dpi-header-titles">
          <h2 className="admin-section-title">{t("admin.insights.title")}</h2>
          <p className="admin-section-subtitle">{t("admin.insights.subtitle")}</p>
        </div>
      </div>

      {/* Outlet expands to fill the leftover space; dates, clear and the
          manage button stay grouped on the right. */}
      <div className="dpi-filter-row">
        <div className="dpi-field dpi-field--outlet">
          <label>{t("admin.insights.filters.outlet")}</label>
          <OutletFilterSelect
            outlets={outlets}
            value={filters.outletId}
            onChange={(outletId) => setFilters((prev) => ({ ...prev, outletId }))}
          />
        </div>
        <div className="dpi-filter-row-right">
          <div className="dpi-field">
            <label>{t("admin.displayProofs.filters.startDate")}</label>
            <input
              type="date"
              className="dpi-input"
              value={filters.startDate}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, startDate: event.target.value }))
              }
            />
          </div>
          <div className="dpi-field">
            <label>{t("admin.displayProofs.filters.endDate")}</label>
            <input
              type="date"
              className="dpi-input"
              value={filters.endDate}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, endDate: event.target.value }))
              }
            />
          </div>
          <button
            type="button"
            className="dpi-btn dpi-btn-secondary"
            onClick={() => setFilters(buildDefaultFilters())}
            disabled={!hasActiveFilter}
          >
            {t("admin.common.clear")}
          </button>
          {isSuperAdmin ? (
            <button
              type="button"
              className="dpi-btn dpi-btn-ghost"
              onClick={() => setIsManageCompetitorsOpen(true)}
            >
              <Pencil size={14} />
              {t("admin.competitors.button")}
            </button>
          ) : null}
        </div>
      </div>

      {/* ── Summary KPIs ─────────────────────────────────────── */}
      <section className="dpi-kpis">
        {kpis.map((kpi) => (
          <div key={kpi.key} className={`dpi-kpi${kpi.tone ? ` dpi-kpi--${kpi.tone}` : ""}`}>
            <span className="dpi-kpi-icon">{kpi.icon}</span>
            <div className="dpi-kpi-body">
              <span className="dpi-kpi-label">{kpi.label}</span>
              <span className={`dpi-kpi-value${kpi.small ? " dpi-kpi-value--sm" : ""}`}>
                {loading && kpi.key !== "lastActivity" ? "—" : kpi.value}
              </span>
            </div>
          </div>
        ))}
      </section>

      {/* ── Our Outlet Products ──────────────────────────────── */}
      <section className="dpi-section dpi-section--own">
        <div className="dpi-section-head">
          <div className="dpi-section-title">
            <span className="dpi-section-icon dpi-section-icon--own">
              <Package size={17} />
            </span>
            <div>
              <h3>{t("admin.insights.outletProducts.title")}</h3>
              <p>{t("admin.insights.outletProducts.subtitle")}</p>
            </div>
          </div>
          <span className="dpi-pill dpi-pill--own">
            {t("admin.insights.outletProducts.outletCount", { count: outletProducts.length })}
          </span>
        </div>
        <OutletProductTable outlets={outletProducts} loading={loading} />
      </section>

      {/* ── Competitor Activity ──────────────────────────────── */}
      <section className="dpi-section dpi-section--comp">
        <div className="dpi-section-head">
          <div className="dpi-section-title">
            <span className="dpi-section-icon dpi-section-icon--comp">
              <Users size={17} />
            </span>
            <div>
              <h3>{t("admin.insights.competitorActivity.title")}</h3>
              <p>{t("admin.insights.competitorActivity.subtitle")}</p>
            </div>
          </div>
          <span className="dpi-pill dpi-pill--comp">
            {t("admin.insights.competitorActivity.count", { count: competitorActivity.length })}
          </span>
        </div>
        <CompetitorActivityPanel competitors={competitorActivity} loading={loading} />
      </section>

      {/* ── Competitor Intelligence ───────────────────────────── */}
      <section className="dpi-section dpi-section--comp">
        <div className="dpi-section-head">
          <div className="dpi-section-title">
            <span className="dpi-section-icon dpi-section-icon--comp">
              <TrendingUp size={17} />
            </span>
            <div>
              <h3>{t("admin.insights.competitorIntelligence.title")}</h3>
              <p>{t("admin.insights.competitorIntelligence.subtitle")}</p>
            </div>
          </div>
          {/* Scoped to this section only — Outlet × Product and Competitor
              Activity above aren't category-aware yet. */}
          <div className="dpi-ci-category-filter">
            <label>{t("admin.insights.competitorIntelligence.categoryFilter")}</label>
            <ProductGroupSelect
              groups={productGroups}
              value={filters.productGroupId}
              onChange={(productGroupId) =>
                setFilters((prev) => ({ ...prev, productGroupId }))
              }
              placeholder={t("admin.insights.competitorIntelligence.allCategories")}
              allowEmptyLabel={t("admin.insights.competitorIntelligence.allCategories")}
            />
          </div>
        </div>
        <CompetitorIntelligencePanel
          data={competitorIntelligence}
          loading={intelligenceLoading}
          hasDateRange={hasDateRange}
        />
      </section>

      {isSuperAdmin ? (
        <ManageCompetitorsModal
          isOpen={isManageCompetitorsOpen}
          onClose={() => setIsManageCompetitorsOpen(false)}
        />
      ) : null}
    </div>
  );
}
